import React from 'react';
import { MapPin, Mail, Phone, Send } from 'lucide-react';

const Contact = () => {
  return (
    <section className="relative py-24 px-5 bg-[#f8fafc] min-h-screen font-sans overflow-hidden">
      
      {/* Ambient Glow */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-br from-sky-100/50 via-transparent to-transparent pointer-events-none"></div>

      {/* Google Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .font-serif {
          font-family: 'Playfair Display', serif;
        }
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }
      `}</style>
      
      <div className="max-w-[1200px] mx-auto relative z-10 mt-[60px]">
        {/* Header Section */}
        <div className="text-center mb-16 space-y-4 animate-in fade-in slide-in-from-top-4 duration-700">
          <h4 className="text-[#0284c7] font-bold text-[13px] uppercase tracking-[0.15em] font-inter mb-4 block">
            Contact Us
          </h4>
          <h2 className="text-[40px] md:text-[56px] font-serif text-[#0f172a] font-normal leading-tight tracking-tight">
            Let's Start a <span className="text-[#14b8a6] italic">Conversation</span>
          </h2>
          <p className="text-slate-500 text-[16px] md:text-[17px] font-medium max-w-2xl mx-auto leading-relaxed font-inter mt-4">
            Have questions about our internships or courses? We're here to help you accelerate your tech career.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
          
          {/* Left Column: Contact Info Cards */}
          <div className="lg:col-span-4 space-y-6">
            <ContactInfoCard 
              icon={<MapPin className="text-[#0ea5e9]" size={22} strokeWidth={2} />} 
              title="Visit Us" 
              detail="Hyderabad, Telangana, India" 
            />
            <ContactInfoCard 
              icon={<Mail className="text-[#0ea5e9]" size={22} strokeWidth={2} />} 
              title="Email Us" 
              detail="contact@senvra.ai" 
            />
            <ContactInfoCard 
              icon={<Phone className="text-[#0ea5e9]" size={22} strokeWidth={2} />} 
              title="Call Us" 
              detail="+91 9336576683" 
            />
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-8 bg-white rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-slate-100 relative overflow-hidden">
            {/* Gradient Top Border */}
            <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-[#0ea5e9] via-[#a855f7] to-[#f43f5e]"></div>
            
            <form className="p-8 md:p-12 space-y-8">
              <h3 className="text-[26px] md:text-[28px] font-serif font-semibold text-[#0f172a] mb-2 tracking-wide">
                Send us a message
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  <label className="text-[13px] font-bold text-[#334155] font-inter">
                    Full Name
                  </label>
                  <input 
                    type="text" 
                    placeholder="John Doe" 
                    className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3.5 outline-none focus:border-[#0284c7] focus:ring-1 focus:ring-[#0284c7]/20 transition-all font-inter text-[15px] text-slate-800"
                  />
                </div>
                <div className="space-y-2.5">
                  <label className="text-[13px] font-bold text-[#334155] font-inter">
                    Email Address
                  </label>
                  <input 
                    type="email" 
                    placeholder="john@example.com" 
                    className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3.5 outline-none focus:border-[#0284c7] focus:ring-1 focus:ring-[#0284c7]/20 transition-all font-inter text-[15px] text-slate-800"
                  />
                </div>
              </div>

              <div className="space-y-2.5">
                <label className="text-[13px] font-bold text-[#334155] font-inter">
                  Phone Number
                </label>
                <input 
                  type="text" 
                  placeholder="+91 98765 43210" 
                  className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3.5 outline-none focus:border-[#0284c7] focus:ring-1 focus:ring-[#0284c7]/20 transition-all font-inter text-[15px] text-slate-800"
                />
              </div>

              <div className="space-y-2.5">
                <label className="text-[13px] font-bold text-[#334155] font-inter">
                  Your Message
                </label>
                <textarea 
                  rows="4" 
                  placeholder="How can we help you?" 
                  className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3.5 outline-none focus:border-[#0284c7] focus:ring-1 focus:ring-[#0284c7]/20 transition-all font-inter text-[15px] text-slate-800 resize-none"
                ></textarea>
              </div>

              <button className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-md group font-inter text-[15px]">
                Send Message
                <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

// Reusable Info Card Component
const ContactInfoCard = ({ icon, title, detail }) => (
  <div className="bg-white p-6 md:p-8 rounded-[24px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center gap-5 group hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 cursor-default">
    <div className="w-[50px] h-[50px] bg-[#f0f9ff] rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <div>
      <h4 className="text-[17px] font-bold text-[#0f172a] mb-1 font-serif tracking-wide">
        {title}
      </h4>
      <p className="text-slate-500 text-[14px] font-medium font-inter">
        {detail}
      </p>
    </div>
  </div>
);

export default Contact;